import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useDocumentAcceptancesRequests } from "../../api/legal";
import { LegalDocument } from "../../types/legal";
import { DOCUMENT_LABELS, sortDocuments } from "../ui/legalLinks/legalLinks.component";
import { useAuthContext } from "../../contexts/auth";
import { ERROR_SNACKBAR } from "../../consts";
import styles from "./legalAcceptanceModal.module.css";

/**
 * Blocks the app until the user has dealt with every document that still awaits
 * their confirmation - a first-login acknowledgement for someone whose account
 * was created by their employer, or a re-acceptance after a new version was
 * published. Deliberately not dismissible: the only way past it is to confirm
 * or to log out.
 */
export const LegalAcceptanceModal = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser, logout } = useAuthContext();
  const { queryPending, accept, loading } = useDocumentAcceptancesRequests();

  const [pending, setPending] = useState<LegalDocument[]>([]);
  const [checked, setChecked] = useState<Record<number, boolean>>({});

  const refresh = useCallback(() => {
    queryPending().then((documents) => setPending(sortDocuments(documents)));
  }, [queryPending]);

  useEffect(() => {
    if (!currentUser) return;
    refresh();
  }, [currentUser, refresh]);

  // Contracting documents are a fresh agreement and need an explicit tick each;
  // acknowledgements only need to be seen, so a single button covers them.
  const contracting = pending.filter((document) => document.requirement === "C");
  const acknowledgements = pending.filter((document) => document.requirement === "A");
  const allTicked = contracting.every((document) => checked[document.id]);

  const handleConfirm = async () => {
    const { success } = await accept(pending.map((document) => document.id));
    if (!success) {
      enqueueSnackbar(t("Could not save your confirmation. Please try again."), ERROR_SNACKBAR);
      return;
    }
    setChecked({});
    refresh();
  };

  if (!pending.length) {
    return null;
  }

  const renderDocumentLink = (document: LegalDocument) => (
    <Link href={document.url} target="_blank" rel="noopener noreferrer" underline="hover">
      {t(DOCUMENT_LABELS[document.kind])} ({document.version})
    </Link>
  );

  return (
    <Dialog open disableEscapeKeyDown maxWidth="sm" fullWidth>
      <DialogTitle>{t("Documents you need to confirm")}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" className={styles.intro}>
          {t("Before you continue, please review the documents below. You can read each of them in a new tab.")}
        </Typography>

        {contracting.length > 0 && (
          <Box className={styles.section}>
            {contracting.map((document) => (
              <Box key={document.id} className={styles.documentRow}>
                <FormControlLabel
                  className={styles.checkboxLabel}
                  control={
                    <Checkbox
                      size="small"
                      checked={Boolean(checked[document.id])}
                      onChange={(event) =>
                        setChecked((prev) => ({ ...prev, [document.id]: event.target.checked }))
                      }
                    />
                  }
                  label={
                    <Typography variant="body2">
                      {t("I accept")} {renderDocumentLink(document)}
                    </Typography>
                  }
                />
                {document.changelog && (
                  <Typography variant="caption" color="text.secondary" className={styles.changelog}>
                    {t("What changed")}: {document.changelog}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        )}

        {acknowledgements.length > 0 && (
          <Box className={styles.section}>
            {acknowledgements.map((document) => (
              <Box key={document.id} className={styles.documentRow}>
                <Typography variant="body2">• {renderDocumentLink(document)}</Typography>
                {document.changelog && (
                  <Typography variant="caption" color="text.secondary" className={styles.changelog}>
                    {t("What changed")}: {document.changelog}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => logout()} color="inherit">
          {t("Logout")}
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!allTicked}
          loading={loading}
        >
          {contracting.length > 0 ? t("Accept and continue") : t("I understand")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
